<template>
  <teleport to="body">
    <Dialog v-model:visible="isDialogVisible" modal :header="t('certificates.title')" :style="{ width: '50rem' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }" @show="onDialogShow" @hide="onDialogHide">
      <Listbox v-model="selectedCertificate" :options="certificates" optionLabel="name" class="w-full md:w-56 gap-4 mb-4" listStyle="max-height:250px">
        <template #option="slotProps">
          <div class="flex items-center">
            <div class="icon-sign"></div>
            <div>
              <p>{{ slotProps.option.name }}</p>
              <p>{{ slotProps.option.thumbprint }}</p>
            </div>
          </div>
        </template>
      </Listbox>
      <div class="flex justify-end gap-2 mb-4">
        <Button type="button" :label="t('dialogs.cancel')" severity="secondary" @click="onCancel"></Button>
        <Button type="button" :label="t('certificates.select')" @click="onSelectCertificate" :disabled="!selectedCertificate"></Button>
      </div>

    </Dialog>
  </teleport>
</template>

<script setup lang="ts">
import Listbox from 'primevue/listbox';
import { useSignModal, type CertificateForSign } from '@/stores/useSignModal';
import { useNotification } from '@/common/composables/useNotification';
import { useI18n } from '@/common/composables/useI18n'
import { ref, watch } from 'vue'
import {
  Certificate,
  getUserCertificates
} from 'crypto-pro'

const store = useSignModal();
const notification = useNotification();
const { t } = useI18n()
const selectedCertificate = ref<Certificate | null>(null);
const certificates = ref<Certificate[]>([]);
const isDialogVisible = ref(false);
let certificatesRequestId = 0;

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  return t('certificates.loadFailed');
}

function onDialogShow() {
  selectedCertificate.value = null;
}

function onDialogHide() {
  if (store.isOpen) {
    store.closeModal();
  }
}

function onCancel() {
  store.resolveCertificateSelection(null);
}

/** Подтверждение выбора сертификата */
function onSelectCertificate() {
  const cert = selectedCertificate.value;
  if (!cert) return;
  const forSign: CertificateForSign = {
    thumbprint: cert.thumbprint,
    name: cert.name,
    subjectName: cert.subjectName,
    validFrom: cert.validFrom,
    validTo: cert.validTo,
  };
  store.resolveCertificateSelection(forSign);
}

watch(
  () => store.isOpen,
  async (isOpen) => {
    if (!isOpen) {
      isDialogVisible.value = false;
      return;
    }
    const requestId = ++certificatesRequestId;
    try {
      certificates.value = await getUserCertificates();
      if (!store.isOpen || requestId !== certificatesRequestId) return;
      isDialogVisible.value = true;
    } catch (error) {
      console.error(error);
      if (requestId === certificatesRequestId) {
        notification.error({
          summary: t('certificates.loadErrorSummary'),
          detail: getErrorMessage(error),
          life: 5000,
        });
        store.closeModal();
      }
    }
  }
);
</script>


<style scoped lang="scss">
.icon-sign {
  min-width: 32px;
  min-height: 32px;
  background-image: url(data:image/svg+xml,%3Csvg%20width%3D%2732%27%20height%3D%2732%27%20viewBox%3D%270%200%2032%2032%27%20fill%3D%27none%27%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%3E%3Cpath%20d%3D%27M12.947%2030.2697C11.8839%2029.4131%2010.8284%2028.5565%209.75767%2027.7152C9.66589%2027.6387%209.48998%2027.654%209.35231%2027.6616C8.18212%2027.7228%207.01192%2027.7916%205.83408%2027.8605C5.727%2027.8681%205.62758%2027.8605%205.47461%2027.8605C6.25474%2025.4283%207.02722%2023.0114%207.7997%2020.5869C8.13623%2020.6405%208.37333%2020.7552%208.54924%2021.0611C8.94695%2021.7648%209.3982%2022.4455%209.81121%2023.1338C9.90299%2023.2791%209.98712%2023.3403%2010.1707%2023.3097C11.012%2023.195%2011.8533%2023.0956%2012.6946%2023.0038C12.8476%2022.9885%2013.0388%2023.0344%2013.1688%2023.1109C13.6736%2023.4091%2014.1631%2023.7457%2014.6679%2024.0516C14.8438%2024.1587%2014.8821%2024.2581%2014.8209%2024.457C14.2243%2026.2849%2013.643%2028.1205%2013.0541%2029.9485C13.0235%2030.0479%2013.0082%2030.1473%2012.9853%2030.2544C12.9853%2030.2697%2012.97%2030.2697%2012.947%2030.2697Z%27%20fill%3D%27%2347A583%27%2F%3E%3Cpath%20d%3D%27M18.0499%2030.2689C17.8281%2029.5499%2017.614%2028.831%2017.3922%2028.1197C17.0098%2026.9036%2016.6197%2025.6875%2016.222%2024.4715C16.1531%2024.2649%2016.199%2024.1732%2016.3749%2024.0661C16.8644%2023.7678%2017.3463%2023.4619%2017.8205%2023.1406C18.004%2023.0183%2018.1876%2022.9724%2018.4094%2023.003C19.2354%2023.1101%2020.0538%2023.2018%2020.8798%2023.3166C21.0634%2023.3395%2021.1552%2023.286%2021.2393%2023.1406C21.6599%2022.4446%2022.1035%2021.7716%2022.5013%2021.0679C22.6772%2020.762%2022.9219%2020.6626%2023.2508%2020.5938C24.0233%2023.0106%2024.7958%2025.4198%2025.5835%2027.875C25.0558%2027.852%2024.5663%2027.8291%2024.0768%2027.7985C23.2508%2027.7526%2022.4171%2027.699%2021.5911%2027.6608C21.4917%2027.6532%2021.3693%2027.6685%2021.3005%2027.722C20.2374%2028.571%2019.1742%2029.4276%2018.1111%2030.2842C18.0882%2030.2689%2018.0652%2030.2689%2018.0499%2030.2689Z%27%20fill%3D%27%2347A583%27%2F%3E%3Cpath%20d%3D%27M10.9351%202.02296C11.7229%202.12239%2012.4342%202.21417%2013.1455%202.29066C13.2755%202.30595%2013.4285%202.27536%2013.5432%202.20652C14.1398%201.8394%2014.7363%201.45699%2015.3252%201.07457C15.4782%200.975143%2015.5776%200.975143%2015.7306%201.07457C16.3119%201.45699%2016.9008%201.83176%2017.4897%202.19888C17.6044%202.26771%2017.7727%202.30595%2017.9104%202.29066C18.6064%202.22182%2019.2947%202.13004%2019.9831%202.03826C20.159%202.01532%2020.2584%202.0765%2020.3502%202.22947C20.7097%202.82604%2021.0691%203.42261%2021.4439%204.00388C21.5127%204.11096%2021.6428%204.21039%2021.7575%204.25628C22.4229%204.49338%2023.0883%204.72283%2023.7614%204.94463C23.922%204.99817%2023.9832%205.067%2023.9985%205.24291C24.0596%205.96185%2024.1438%206.68845%2024.2279%207.40739C24.2432%207.51447%2024.312%207.63684%2024.3962%207.71332C24.8857%208.21046%2025.3828%208.69996%2025.8876%209.17415C26.0329%209.31182%2026.0406%209.4189%2025.9794%209.59481C25.7346%2010.2449%2025.4975%2010.9027%2025.2757%2011.5604C25.2375%2011.6828%2025.2375%2011.8434%2025.2757%2011.9658C25.5052%2012.6312%2025.7423%2013.2966%2025.9947%2013.9544C26.0559%2014.1073%2026.0329%2014.2068%2025.9105%2014.3215C25.3981%2014.811%2024.901%2015.3081%2024.4038%2015.8129C24.312%2015.9047%2024.2432%2016.05%2024.2279%2016.18C24.1361%2016.8913%2024.0749%2017.6026%2023.9832%2018.3139C23.9679%2018.4133%2023.8531%2018.5357%2023.7537%2018.574C23.1036%2018.8111%2022.4382%2019.0176%2021.7804%2019.2623C21.6428%2019.3158%2021.5051%2019.4306%2021.421%2019.5529C21.0462%2020.1266%2020.6867%2020.7155%2020.3349%2021.3044C20.2431%2021.4497%2020.159%2021.5109%2019.9754%2021.488C19.2794%2021.3885%2018.5758%2021.3121%2017.8721%2021.2432C17.7421%2021.2279%2017.5892%2021.2738%2017.4744%2021.3427C16.8779%2021.7021%2016.2889%2022.0769%2015.7153%2022.467C15.5623%2022.5664%2015.4553%2022.5587%2015.3099%2022.4593C14.744%2022.0845%2014.178%2021.7174%2013.5967%2021.3732C13.4514%2021.2815%2013.2373%2021.2432%2013.069%2021.2585C12.3959%2021.3197%2011.7229%2021.4038%2011.0498%2021.4956C10.8816%2021.5186%2010.7898%2021.4803%2010.7057%2021.335C10.3538%2020.7461%2010.002%2020.1572%209.61959%2019.5835C9.52781%2019.4382%209.35955%2019.3158%209.19129%2019.2547C8.55647%2019.0176%207.91401%2018.8034%207.27155%2018.5969C7.11094%2018.5434%207.04975%2018.4745%207.03446%2018.2986C6.97327%2017.5797%206.88914%2016.8531%206.80501%2016.1341C6.78971%2016.0347%206.72852%2015.92%206.65204%2015.8435C6.16255%2015.3464%205.67305%2014.8569%205.16061%2014.375C5%2014.2221%204.9847%2014.1073%205.06119%2013.9085C5.29828%2013.289%205.52773%2012.6618%205.73424%2012.027C5.78778%2011.874%205.78013%2011.6752%205.72659%2011.5145C5.51244%2010.8644%205.27534%2010.222%205.03059%209.57952C4.96941%209.4189%205%209.32712%205.12237%209.2124C5.62716%208.73055%206.1243%208.24106%206.6138%207.74392C6.71323%207.64449%206.78206%207.49152%206.80501%207.35385C6.89679%206.64256%206.96562%205.93126%207.03446%205.21997C7.04975%205.04406%207.14918%205.00581%207.2792%204.95992C7.93696%204.73812%208.59472%204.51632%209.25247%204.27922C9.38249%204.23333%209.52016%204.12626%209.59665%204.01153C9.97906%203.41496%2010.3385%202.81074%2010.7133%202.21417C10.7821%202.11474%2010.8892%202.06121%2010.9351%202.02296ZM8.12052%2011.7516C8.11287%2015.8282%2011.4169%2019.1476%2015.4935%2019.1629C19.6007%2019.1782%2022.9353%2015.8664%2022.9353%2011.7593C22.9353%207.66743%2019.6236%204.34806%2015.5318%204.34041C11.4475%204.34041%208.12052%207.65979%208.12052%2011.7516Z%27%20fill%3D%27%2347A583%27%2F%3E%3Cpath%20d%3D%27M21.2149%2011.7682C21.2149%2014.9193%2018.6603%2017.4585%2015.5092%2017.4509C12.3734%2017.4432%209.83417%2014.8963%209.84181%2011.7529C9.84946%208.61706%2012.3887%206.07017%2015.5245%206.06252C18.6527%206.05487%2021.2149%208.61706%2021.2149%2011.7682Z%27%20fill%3D%27%2347A583%27%2F%3E%3C%2Fsvg%3E);
}
</style>
